import React, { useState } from "react";
import { omit } from "lodash";
import { ScanReportType, WizardStep } from "@sscs/types";
import { Dialog, Toast, Wizard } from "@sscs/ui";
import { useGetWhoamiQuery } from "Services";
import { apiErrorToast } from "Utils";
import { useShareReportMutation } from "../../../services/sharedReports.api";
import {
  SharedReportForm,
  SharedReportInfo,
  SharedReportLinkNameForm,
  SharedReportRecipientsForm,
  SharedReportRestrictionsForm,
} from "../../../types";
import {
  DEFAULT_SETTINGS,
  getLinkNameStepInitialValues,
  getRecipientsFormInitialValues,
  getRestrictionsStepInitialValues,
  getSharedReportLinkNameSchema,
  getSharedReportRecipientsFormSchema,
  restrictionsFormSchema,
} from "../../../utils";
import commonStyles from "../../common.module.scss";
import WizardSteps from "../common/WizardSteps";
import { ShareReportWizardStep, WIZARD_STEPS_LABELS } from "./constants";

type ShareReportStepForm =
  | SharedReportLinkNameForm
  | SharedReportRestrictionsForm
  | SharedReportRecipientsForm;

type ShareReportDialogProps = {
  purl: string;
  onClose: () => void;
  isOpen: boolean;
  versionId: string;
  reportType?: ScanReportType;
  contactSupport?: boolean;
};

const ShareReportDialogContent = ({
  purl,
  onClose,
  versionId,
  reportType,
  contactSupport,
}: Omit<ShareReportDialogProps, "isOpen">): React.JSX.Element => {
  const [activeWizardStep, setActiveWizardStep] = useState(
    ShareReportWizardStep.LinkNameStep,
  );
  const [settings, setSettings] = useState<SharedReportForm>(DEFAULT_SETTINGS);
  const [sharedReport, setSharedReport] = useState<SharedReportInfo | null>(
    null,
  );
  const [skipRecipients, setSkipRecipients] = useState(false);

  const handleNext = (data?: ShareReportStepForm) => {
    if (data) {
      updateSharedReportSettings({ ...settings, ...data });
    }
    setActiveWizardStep((previous) => previous + 1);
  };

  const handlePrevious = (data?: ShareReportStepForm): void => {
    if (data) {
      updateSharedReportSettings({ ...settings, ...data });
    }
    setActiveWizardStep((previous) => previous - 1);
  };

  const { data: whoami } = useGetWhoamiQuery();

  const [shareReport, { isLoading: isSharingReport }] =
    useShareReportMutation();

  const generateLink = async (): Promise<boolean> => {
    try {
      const recipients = settings.recipients ? [...settings.recipients] : [];

      if (settings.send_copy_to_me && whoami?.email && recipients.length > 0) {
        recipients.push(whoami.email);
      }

      const sharedReportResponse = await shareReport({
        version_id: versionId,
        report_type: reportType,
        settings: {
          ...omit(settings, ["use_case", "send_copy_to_me"]),
          recipients,
          message: recipients.length > 0 ? settings.message : "",
        },
      }).unwrap();

      setSharedReport(sharedReportResponse);
      Toast.show({
        title: "The report has been shared successfully",
        type: "success",
      });
      return true;
    } catch (error) {
      apiErrorToast(error, "Could not generate link");
      return false;
    }
  };

  const updateSharedReportSettings = (data: SharedReportForm) => {
    setSettings((prevState) => ({
      ...prevState,
      ...data,
    }));
  };

  return (
    <Wizard
      title="Share report link"
      subtitle={<code>{purl}</code>}
      activeStep={activeWizardStep}
      steps={
        [
          {
            label: WIZARD_STEPS_LABELS[ShareReportWizardStep.LinkNameStep],
            component: <WizardSteps.LinkNameStep />,
            formValues: getLinkNameStepInitialValues(settings, contactSupport),
            schema: getSharedReportLinkNameSchema(),
            onNext: handleNext,
            onPrevious: handlePrevious,
          },
          {
            label: WIZARD_STEPS_LABELS[ShareReportWizardStep.RestrictionsStep],
            component: <WizardSteps.RestrictionsStep />,
            formValues: getRestrictionsStepInitialValues(
              settings.label,
              settings,
            ),
            schema: restrictionsFormSchema,
            formProps: { dense: true },
            onNext: handleNext,
            onPrevious: handlePrevious,
          },
          {
            label: WIZARD_STEPS_LABELS[ShareReportWizardStep.RecipientsStep],
            isOptional: skipRecipients,
            component: (
              <WizardSteps.RecipientsStep
                onSkipRecipients={setSkipRecipients}
              />
            ),
            formValues: getRecipientsFormInitialValues(
              settings.label,
              settings,
            ),
            formMode: "onChange",
            schema: getSharedReportRecipientsFormSchema(false),
            formProps: { dense: true },
            onNext: handleNext,
            onPrevious: handlePrevious,
          },
          {
            label: WIZARD_STEPS_LABELS[ShareReportWizardStep.ReviewStep],
            nextStep: { label: "Generate link", isLoading: isSharingReport },
            component: (
              <WizardSteps.ReviewStep
                formData={settings}
                resentToEmailAddressesCount={0}
                sentToEmailAddressesCount={settings.recipients?.length ?? 0}
              />
            ),
            onNext: async () => {
              if (await generateLink()) {
                handleNext();
              }
            },
            onPrevious: handlePrevious,
          },
          {
            label: WIZARD_STEPS_LABELS[ShareReportWizardStep.ShareReportStep],
            previousStep: { isHidden: true },
            component: (
              <WizardSteps.ShareReportStep
                sharedReport={sharedReport}
                sentToEmailAddressesCount={sharedReport?.recipients.count}
              />
            ),
            onNext: onClose,
          },
        ] satisfies readonly [
          WizardStep<SharedReportLinkNameForm>,
          WizardStep<SharedReportRestrictionsForm>,
          WizardStep<SharedReportRecipientsForm>,
          WizardStep,
          WizardStep,
        ]
      }
      onStepChange={setActiveWizardStep}
      dialogContentClasses={commonStyles["dialog-content"]}
    />
  );
};

const ShareReportDialog = ({
  isOpen,
  ...props
}: ShareReportDialogProps): React.JSX.Element => (
  <Dialog.Frame isOpen={isOpen} onClose={props.onClose} size="md">
    <ShareReportDialogContent {...props} />
  </Dialog.Frame>
);

export default ShareReportDialog;
